import {
  cloneSelectionSnapshot,
  createClearedSnapshot,
  isoNow,
  parseSelectionSnapshot,
} from "./snapshot.ts";
import type { SelectionListener, SelectionReader, SelectionSnapshot } from "./types.ts";

export type SelectionStoreOptions = {
  /** Injected clock for tests. */
  now?: () => Date | string;
};

/**
 * Single selection truth for the editor package.
 * Adapters write via `set` / `clear`. Think reads `asSelectionReader(store)`.
 */
export class SelectionStore implements SelectionReader {
  #snapshot: SelectionSnapshot;
  #listeners = new Set<SelectionListener>();
  readonly #now: () => Date | string;

  constructor(options: SelectionStoreOptions = {}) {
    this.#now = options.now ?? (() => new Date());
    this.#snapshot = createClearedSnapshot(isoNow(this.#now));
  }

  get(): SelectionSnapshot {
    return cloneSelectionSnapshot(this.#snapshot);
  }

  /** Adapter / host write path. Think must not call this. */
  set(snapshot: SelectionSnapshot): void {
    this.#snapshot = cloneSelectionSnapshot(parseSelectionSnapshot(snapshot));
    this.#emit();
  }

  clear(at?: string): void {
    this.set(createClearedSnapshot(at ?? isoNow(this.#now)));
  }

  subscribe(listener: SelectionListener): () => void {
    this.#listeners.add(listener);
    return () => {
      this.#listeners.delete(listener);
    };
  }

  #emit(): void {
    for (const listener of this.#listeners) {
      try {
        listener(this.get());
      } catch {
        /* one subscriber must not break the others */
      }
    }
  }
}

export function createSelectionStore(options?: SelectionStoreOptions): SelectionStore {
  return new SelectionStore(options);
}

/** Strip write methods for Think / session injection. */
export function asSelectionReader(store: SelectionStore): SelectionReader {
  return {
    get: () => store.get(),
    subscribe: (listener) => store.subscribe(listener),
  };
}
